import React from 'react'
import { cn } from '@/utils/cn'

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface SpinnerProps {
  size?: SpinnerSize
  className?: string
  color?: string
}

const sizeMap: Record<SpinnerSize, number> = {
  xs: 14,
  sm: 16,
  md: 20,
  lg: 28,
  xl: 40,
}

export function Spinner({ size = 'md', className, color }: SpinnerProps) {
  const px = sizeMap[size]

  return (
    <svg
      className={cn('animate-spin', className)}
      style={{ color: color || 'var(--accent)', width: px, height: px }}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label="Loading"
      role="status"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  )
}

// Full-page loading overlay
export function PageSpinner({ message }: { message?: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        {message && (
          <p className="text-sm text-steel dark:text-slate-400">{message}</p>
        )}
      </div>
    </div>
  )
}

// Inline loading state
export function InlineSpinner({ message }: { message?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-4">
      <Spinner size="sm" />
      {message && (
        <span className="text-sm text-steel dark:text-slate-400">{message}</span>
      )}
    </div>
  )
}

export default Spinner
